import React, { useContext, useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import { useMediaQuery } from 'react-responsive';
import { Box, Button, Grid, Typography } from '@mui/material';
import {
  CreateOutlined,
  DeleteOutline,
  MailOutlined
} from '@mui/icons-material';
import CustomHead from '../CustomHead';
import CustomLoading from '../../uiParts/CustomLoading';
import SideBar from '../../uiParts/SideBar';
import {
  gray100,
  gray70,
  gray90,
  primary10,
  primaryButtonBackgroundColor,
  primaryButtonOutlinedColor,
  tertiaryButtonOutlinedColor
} from '../../../constants/Colors';
import CustomSnackbar from '../../uiParts/CustomSnackbar';
import { sideBarWidth } from '../../../constants/Location';
import { AuthContext } from '../../../contexts/auth';
import { useNavigate } from 'react-router-dom';
import { userRoleEnum } from '../../../constants/Enum';
import DeleteMemberModal from '../../uiParts/DeleteMemberModal';
import EditMemeberModal from '../../uiParts/EditMemeberModal';
import InviteMemeberModal from '../../uiParts/InviteMemeberModal';
import {
  getMemberList,
  updateMemberRoleLeader,
  memberWithdraw,
  sendMailForResisterMembers
} from '../../../api/db/member';
import { sentryLog } from '../../../config/sentry';
import { common } from '../../../modules/error';
import { response } from '../../../api/db';
import { InviteCompleteMemeberModal } from '../../uiParts/InviteCompleteMemeberModal';
import { MemberShowType } from '../../../validation/user/userType';

const styles = {
  wrapper: {
    m: '32px',
    '@media screen and (max-width:600px)': {
      mb: '144px'
    }
  },
  headerContainer: {
    mb: '25px'
  },
  title: {
    '@media screen and (max-width:600px)': {
      fontSize: '24px'
    },
    fontSize: '32px',
    color: gray100,
    fontWeight: 500
  },
  saveButton: {
    ...primaryButtonBackgroundColor,
    p: '10px 16px',
    borderRadius: '4px',
    fontSize: '16px',
    height: '44px',
    '@media screen and (max-width:960px)': {
      display: 'none'
    }
  },
  spButtonContainer: {
    width: '100vw',
    backgroundColor: 'rgba(255, 255, 255, 0.80)',
    p: '12px 16px',
    position: 'fixed',
    bottom: '0',
    left: '0',
    zIndex: 1,
    '@media screen and (min-width:961px)': {
      display: 'none'
    }
  },
  saveButtonSp: {
    ...primaryButtonBackgroundColor,
    p: '10px 16px',
    borderRadius: '4px',
    fontSize: '16px',
    width: '100%',
    height: '44px'
  },
  tableContainer: {
    border: '1px solid' + gray70,
    borderBottom: 'none',
    minWidth: '1100px'
  },
  headCell: {
    fontSize: '16px',
    fontWeight: 500,
    color: gray100,
    background: primary10,
    height: '44px',
    p: '10px 32px',
    borderRight: '1px solid' + gray70,
    borderBottom: '1px solid' + gray70
  },
  cell: {
    fontSize: '16px',
    fontWeight: 500,
    color: gray100,
    borderRight: '1px solid' + gray70,
    borderBottom: '1px solid' + gray70,
    display: 'flex',
    alignItems: 'center',
    p: '12.5px 32px'
  },
  reSendButton: {
    ...primaryButtonOutlinedColor,
    boxSizing: 'border-box',
    width: '196px'
  },
  createButton: {
    ...primaryButtonOutlinedColor,
    boxSizing: 'border-box',
    p: '5px 16px',
    width: '90px'
  },
  deleteButton: {
    ...tertiaryButtonOutlinedColor,
    boxSizing: 'border-box',
    p: '5px 16px',
    width: '90px'
  },
  remainingMember: {
    fontSize: '16px',
    color: gray90
  }
};

const Members = (): React.ReactElement => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState<string>('');
  const [infoText, setInfoText] = useState<string | null | undefined>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const { checkLoginStatus } = useContext(AuthContext);
  const [userRole, setUserRole] = useState<number | null>(null);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [inviteCompleteModalOpen, setInviteCompleteModalOpen] = useState(false);
  const [memberRole, setMemberRole] = useState<number | null>(null);
  const [memberInfo, setMemberInfo] = useState<{
    id: number;
    status: number;
  } | null>(null);
  const isMobile = useMediaQuery({ query: '(max-width: 960px)' });

  // 画面アクセス制御
  useEffect(() => {
    void (async () => {
      setLoading(true);
      if (!checkLoginStatus) return navigate('/auth/login');
      const result = await checkLoginStatus();
      if (!result.check) {
        navigate('/auth/login');
        return;
      }
      setUserRole(result.role);
    })();
  }, [checkLoginStatus]);

  // エラーハンドリング
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleError = (error: any) => {
    const axiosError = error as AxiosError;
    if (error instanceof Error) {
      sentryLog(error);
      if (axiosError.isAxiosError && axiosError.response) {
        const errorResponse = axiosError.response as response;
        setErrorText(errorResponse.data.message);
        setSnackbarOpen(true);
      } else {
        setErrorText(common);
        setSnackbarOpen(true);
      }
    }
  };

  const clearSnackbarText = () => {
    setErrorText('');
    setInfoText('');
  };

  const [members, setMembers] = useState<MemberShowType[]>([]);
  const fetchMemberData = async () => {
    setLoading(true);
    clearSnackbarText();
    try {
      const response = await getMemberList();

      // 表示用データに変換
      const { members } = response.data;
      const formattedMembers = Object.entries(members).map(([key, member]) => {
        return {
          key,
          id: member.user_id,
          status: member.status,
          email: member.email,
          role: member.role,
          firstName: member.first_name,
          firstNameKana: member.first_name_kana,
          lastName: member.last_name,
          lastNameKana: member.last_name_kana,
          createdAt: member.created_at,
          updatedAt: member.updated_at
        };
      });
      setMembers(formattedMembers);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  // 共有者APIの呼び出し
  useEffect((): void => {
    void (async () => {
      await fetchMemberData();
    })();
  }, []);

  // 共有者数のチェック
  const checkMemberCount = (): boolean => {
    clearSnackbarText();
    if (members.length >= 3) {
      setErrorText(
        '招待できる共有者は2名までです。新しい共有者を招待するには、利用中の共有者を削除してください。'
      );
      setSnackbarOpen(true);
      return false;
    }
    return true;
  };

  //メンバー招待APIの呼び出し
  const onInvite = async (email: string): Promise<void> => {
    try {
      setLoading(true);
      clearSnackbarText();
      await sendMailForResisterMembers(email);
      setInviteCompleteModalOpen(true);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      handleError(error);
    } finally {
      setInviteModalOpen(false);
      setLoading(false);
    }
  };

  //権限変更のAPIの呼び出し
  const onEdit = async (
    selectedRole: number | undefined | null
  ): Promise<void> => {
    try {
      setLoading(true);
      clearSnackbarText();

      if (memberInfo && selectedRole === userRoleEnum.leader) {
        await updateMemberRoleLeader(memberInfo.id);
        setSnackbarOpen(true);
        setInfoText('権限を変更しました');
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      handleError(error);
    } finally {
      setLoading(false);
      setEditModalOpen(false);
      window.location.reload();
    }
  };

  // メンバー退会APIの呼び出し
  const onDelete = async (): Promise<void> => {
    try {
      if (memberInfo) {
        setLoading(true);
        clearSnackbarText();
        await memberWithdraw(memberInfo.id, memberInfo.status === 2);
        await fetchMemberData();
        setSnackbarOpen(true);
        setInfoText('共有者を削除しました');
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      handleError(error);
    } finally {
      setLoading(false);
      setDeleteModalOpen(false);
    }
  };

  return (
    <>
      <CustomHead title="共有者一覧 | CMCOS[コムコス]" />
      <CustomLoading loading={loading} />
      <Grid width="100vw">
        <Box sx={{ position: 'relative' }}>
          <SideBar />
        </Box>
        {userRole === userRoleEnum.leader && isMobile && (
          <Grid
            sx={styles.spButtonContainer}
            container
            alignItems="center"
            justifyContent="end"
            gap={'12px'}
          >
            <Button
              sx={styles.saveButtonSp}
              onClick={() => {
                checkMemberCount() && setInviteModalOpen(true);
              }}
            >
              担当者を招待する
            </Button>
          </Grid>
        )}
      </Grid>
      <Grid
        sx={{
          '@media screen and (min-width:961px)': {
            ml: sideBarWidth
          }
        }}
      >
        <Box sx={styles.wrapper}>
          <Grid
            container
            justifyContent="space-between"
            sx={styles.headerContainer}
          >
            <Grid item>
              <Typography sx={styles.title}>共有者一覧</Typography>
            </Grid>
            {userRole === userRoleEnum.leader && (
              <Grid item>
                <Button
                  sx={styles.saveButton}
                  onClick={() => {
                    checkMemberCount() && setInviteModalOpen(true);
                  }}
                >
                  共有者を招待する
                </Button>
              </Grid>
            )}
          </Grid>
          <Grid container sx={styles.tableContainer}>
            <Grid container>
              <Grid item xs={3} sx={styles.headCell}>
                <Typography>共有者名</Typography>
              </Grid>
              <Grid item xs={2} sx={styles.headCell}>
                <Typography>権限</Typography>
              </Grid>
              <Grid item xs={3} sx={[styles.headCell, { borderRight: 'none' }]}>
                <Typography>メールアドレス</Typography>
              </Grid>
              <Grid
                item
                xs={4}
                sx={[styles.headCell, { borderRight: 'none' }]}
              />
            </Grid>
          </Grid>
          <Grid container sx={styles.tableContainer}>
            {members.map((data, index) => (
              <Grid container key={index}>
                <Grid item xs={3} sx={styles.cell}>
                  <Typography>
                    {data.lastName + ' ' + data.firstName}
                  </Typography>
                </Grid>
                <Grid item xs={2} sx={styles.cell}>
                  <Typography>
                    {data.role === 1 ? '代表者' : '共有者'}
                  </Typography>
                </Grid>
                <Grid item xs={3} sx={[styles.cell, { borderRight: 'none' }]}>
                  <Typography>{data.email}</Typography>
                </Grid>
                {data.role === 1 || userRole === userRoleEnum.member ? (
                  <Grid
                    item
                    xs={4}
                    sx={[
                      styles.cell,
                      { borderRight: 'none', justifyContent: 'flex-end' }
                    ]}
                  ></Grid>
                ) : data.status === 2 ? (
                  <Grid
                    item
                    xs={4}
                    sx={[
                      styles.cell,
                      { borderRight: 'none', justifyContent: 'flex-end' }
                    ]}
                  >
                    <Grid
                      container
                      columnGap={'16px'}
                      rowGap={'4px'}
                      justifyContent={'flex-end'}
                    >
                      <Grid item>
                        <Button
                          startIcon={<MailOutlined />}
                          sx={styles.reSendButton}
                          variant="outlined"
                          onClick={() => onInvite(data.email)}
                        >
                          招待メールを再送信
                        </Button>
                      </Grid>
                      <Grid item>
                        <Button
                          variant="outlined"
                          startIcon={<DeleteOutline />}
                          sx={styles.deleteButton}
                          onClick={() => {
                            setDeleteModalOpen(true);
                            setMemberInfo({ id: data.id, status: data.status });
                          }}
                        >
                          削除
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                ) : (
                  <Grid
                    item
                    xs={4}
                    sx={[
                      styles.cell,
                      { borderRight: 'none', justifyContent: 'flex-end' }
                    ]}
                  >
                    <Grid
                      container
                      columnGap={'16px'}
                      rowGap={'4px'}
                      justifyContent={'space-between'}
                      width={'196px'}
                    >
                      <Grid item>
                        <Button
                          variant="outlined"
                          startIcon={<CreateOutlined />}
                          sx={styles.createButton}
                          onClick={() => {
                            setMemberInfo({ id: data.id, status: data.status });
                            setMemberRole(data.role);
                            setEditModalOpen(true);
                          }}
                        >
                          編集
                        </Button>
                      </Grid>
                      <Grid item>
                        <Button
                          variant="outlined"
                          startIcon={<DeleteOutline />}
                          sx={styles.deleteButton}
                          onClick={() => {
                            setDeleteModalOpen(true);
                            setMemberInfo({ id: data.id, status: data.status });
                          }}
                        >
                          削除
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            ))}
          </Grid>
        </Box>
      </Grid>
      <InviteMemeberModal
        onInvite={onInvite}
        open={inviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
      />
      <InviteCompleteMemeberModal
        open={inviteCompleteModalOpen}
        onClose={async () => {
          await fetchMemberData();
          setInviteCompleteModalOpen(false);
        }}
      />
      <EditMemeberModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onEdit={(selectedRole) => onEdit(selectedRole)}
        role={memberRole}
      />
      <DeleteMemberModal
        setDialogOpen={setDeleteModalOpen}
        onDelete={() => onDelete()}
        open={deleteModalOpen}
        memberId={memberInfo?.id ?? 0}
      />
      <CustomSnackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        text={errorText}
        infoText={infoText}
      />
    </>
  );
};
export default Members;
